import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, api, apiBase, apiBlob, getToken } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { Badge, Card, Empty, ErrorAlert, Spinner } from "../components/ui.tsx";

interface RemoteSession {
  uid: string;
  deviceUid: string;
  deviceName: string;
  status: string;
  requestedBy: string;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  lastFrameAt: string | null;
  frameSeq: number;
  privacy?: number;
  transport?: string;
  error: string;
}

interface RemoteFile {
  uid: string;
  direction: string;
  name: string;
  path: string;
  sizeBytes: number;
  status: string;
  error: string;
  createdAt: string;
  finalPath?: string;
}

type RemoteEvent =
  | { kind: "move"; x: number; y: number }
  | { kind: "down" | "up"; button: "left" | "right" | "middle" }
  | { kind: "wheel"; delta: number }
  | { kind: "key"; vk: number; down: boolean }
  | { kind: "text"; text: string };

const VK: Record<string, number> = {
  Enter: 13, Backspace: 8, Tab: 9, Escape: 27, Delete: 46,
  ArrowLeft: 37, ArrowUp: 38, ArrowRight: 39, ArrowDown: 40,
  Home: 36, End: 35, PageUp: 33, PageDown: 34, Insert: 45,
  Shift: 16, Control: 17, Alt: 18, Meta: 91, CapsLock: 20,
  " ": 32, F1: 112, F2: 113, F3: 114, F4: 115, F5: 116, F6: 117,
  F7: 118, F8: 119, F9: 120, F10: 121, F11: 122, F12: 123,
};

// Codigo VK de caracteres. Sem isto, atalhos como Ctrl+C / Ctrl+V / Ctrl+Z
// nao eram enviados (a tabela VK so tinha teclas especiais).
function charVk(ch: string): number | undefined {
  if (ch.length !== 1) return undefined;
  const up = ch.toUpperCase();
  if (up >= "A" && up <= "Z") return up.charCodeAt(0);
  if (up >= "0" && up <= "9") return up.charCodeAt(0);
  const map: Record<string, number> = {
    "-": 189, "=": 187, "[": 219, "]": 221, "\\": 220, ";": 186,
    "'": 222, ",": 188, ".": 190, "/": 191, "`": 192,
  };
  return map[ch];
}

function humanSize(bytes: number): string {
  if (!bytes) return "—";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function RemotePage() {
  const { sid = "" } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const remoteImgRef = useRef<HTMLImageElement | null>(null);
  const queueRef = useRef<RemoteEvent[]>([]);
  const vkDownRef = useRef<Set<string>>(new Set());
  const lastSeqRef = useRef(0);
  const framesRef = useRef(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const urlRef = useRef<string | null>(null);

  const [session, setSession] = useState<RemoteSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("Conectando...");
  const [frames, setFrames] = useState(0);
  const [recording, setRecording] = useState(false);
  const [files, setFiles] = useState<RemoteFile[]>([]);
  const [showFiles, setShowFiles] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [downloadPath, setDownloadPath] = useState("C:\\Users\\Public\\Downloads\\");
  const [focused, setFocused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [relayBase, setRelayBase] = useState<string | null>(null);
  // LAN so quando o relay esta acessivel E o agente esta de fato usando o relay.
  // Se o PC remoto estiver em outra rede, o servidor informa transport='cloud'.
  const lanMode = Boolean(relayBase) && session?.transport !== "cloud";
  const lanBase = lanMode ? relayBase : null;
  const [uploadDest, setUploadDest] = useState("%DOWNLOADS%");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const ended = session?.status === "ended";

  useEffect(() => {
    let cancelled = false;
    fetch("http://localhost:7788/health", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j && j.ok) setRelayBase("http://localhost:7788"); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.();
  }
  const privacyOn = session?.privacy === 1;

  async function togglePrivacy() {
    if (!sid) return;
    if (lanBase) {
      try {
        await fetch(`${lanBase}/view/${sid}/privacy`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ enabled: !privacyOn }),
        });
      } catch { /* o worker cai para a nuvem */ }
    }
    try {
      const res = await api<{ privacy: number }>(`/api/v3/remote/${sid}/privacy`, {
        method: "POST",
        body: { enabled: !privacyOn },
      });
      setSession((s) => (s ? { ...s, privacy: res.privacy } : s));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao alternar a privacidade");
    }
  }

  // Sessao (status + frameSeq)
  useEffect(() => {
    if (!sid) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api<{ session: RemoteSession }>(`/api/v3/remote/${sid}`);
        if (!cancelled) setSession(res.session);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Falha ao consultar a sessao");
      }
    };
    void tick();
    const id = window.setInterval(tick, 300);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [sid]);

  // Frames por long-poll: o servidor segura a requisicao ate 2s e responde
  // assim que chega um frame novo. Sem polling fixo de 300ms.
  useEffect(() => {
    if (!sid || ended) return;
    let cancelled = false;
    const controller = new AbortController();
    const token = getToken();
    const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const loop = async () => {
      let last = lastSeqRef.current;
      while (!cancelled) {
        try {
          const events = queueRef.current.splice(0, 200);
          // Resolucao adaptativa: no modo LAN o worker captura no tamanho real
          // em que a tela e exibida (janela pequena = rapido, tela cheia = nitido).
          const cvs = canvasRef.current;
          const dpr = window.devicePixelRatio || 1;
          const wantW = cvs ? Math.min(2560, Math.max(640, Math.round(cvs.clientWidth * dpr))) : 0;
          const pollUrl = lanBase
            ? `${lanBase}/view/${sid}/poll?seq=${last}`
            : `${apiBase()}/api/v3/remote/${sid}/poll?seq=${last}`;
          const res = await fetch(pollUrl, {
            method: "POST",
            headers: lanBase
              ? { "content-type": "application/json" }
              : {
                  ...(token ? { authorization: `Bearer ${token}` } : {}),
                  "content-type": "application/json",
                },
            body: JSON.stringify({ seq: last, events, wantW }),
            signal: controller.signal,
            cache: "no-store",
          });
          if (cancelled) break;
          if (res.status === 204) { if (lanBase) await delay(12); continue; }
          if (!res.ok) { await delay(600); continue; }
          const seq = Number(res.headers.get("X-Frame-Seq") || last + 1);
          const blob = await res.blob();
          const bmp = await createImageBitmap(blob);
          if (cancelled) { bmp.close(); break; }
          const canvas = canvasRef.current;
          if (canvas) {
            if (canvas.width !== bmp.width || canvas.height !== bmp.height) {
              canvas.width = bmp.width;
              canvas.height = bmp.height;
            }
            canvas.getContext("2d")?.drawImage(bmp, 0, 0);
          }
          bmp.close();
          last = seq;
          lastSeqRef.current = seq;
          framesRef.current += 1;
          setStatus("Conectado");
        } catch {
          if (cancelled) break;
          await delay(600);
        }
      }
    };
    void loop();
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, ended, lanBase]);

  // Contador de frames sem re-render a cada frame (evita travar a tela).
  useEffect(() => {
    const id = window.setInterval(() => setFrames(framesRef.current), 1000);
    return () => window.clearInterval(id);
  }, []);

  const push = (ev: RemoteEvent) => {
    if (ended) return;
    if (queueRef.current.length > 300) queueRef.current.splice(0, 100);
    queueRef.current.push(ev);
  };

  function normPoint(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    // O canvas e exibido com object-fit: contain, entao a imagem pode ficar
    // menor que a caixa (faixas). Mapear sobre a area REAL da imagem.
    const cw = canvas.width || 1;
    const ch = canvas.height || 1;
    const scale = Math.min(rect.width / cw, rect.height / ch);
    const dispW = cw * scale;
    const dispH = ch * scale;
    const offX = rect.left + (rect.width - dispW) / 2;
    const offY = rect.top + (rect.height - dispH) / 2;
    const x = Math.max(0, Math.min(1, (e.clientX - offX) / dispW));
    const y = Math.max(0, Math.min(1, (e.clientY - offY) / dispH));
    return { x, y };
  }

  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const p = normPoint(e);
    push({ kind: "move", x: p.x, y: p.y });
  }

  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    e.preventDefault();
    boxRef.current?.focus();
    const p = normPoint(e);
    push({ kind: "move", x: p.x, y: p.y });
    push({ kind: "down", button: e.button === 2 ? "right" : e.button === 1 ? "middle" : "left" });
  }

  function onMouseUp(e: React.MouseEvent<HTMLCanvasElement>) {
    e.preventDefault();
    push({ kind: "up", button: e.button === 2 ? "right" : e.button === 1 ? "middle" : "left" });
  }

  function onWheel(e: React.WheelEvent<HTMLCanvasElement>) {
    e.preventDefault();
    push({ kind: "wheel", delta: e.deltaY < 0 ? 120 : -120 });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (ended) return;
    const key = e.key;
    if (key === "F5") return; // deixa recarregar a pagina do painel
    const isShortcut = e.ctrlKey || e.altKey || e.metaKey;

    // Atalho (Ctrl+C, Ctrl+V, Ctrl+Shift+Esc, Alt+Tab...): envia o VK da tecla.
    // Antes, com modificador pressionado, a tecla era descartada e nada chegava
    // ao computador remoto.
    if (isShortcut) {
      const vk = VK[key] ?? charVk(key);
      if (!vk) return;
      e.preventDefault();
      vkDownRef.current.add(key);
      push({ kind: "key", vk, down: true });
      return;
    }

    if (key.length === 1) {
      e.preventDefault();
      push({ kind: "text", text: key });
      return;
    }

    const vk = VK[key];
    if (!vk) return;
    e.preventDefault();
    vkDownRef.current.add(key);
    push({ kind: "key", vk, down: true });
  }

  function onKeyUp(e: React.KeyboardEvent<HTMLDivElement>) {
    const key = e.key;
    if (!vkDownRef.current.has(key)) return;
    vkDownRef.current.delete(key);
    const vk = VK[key] ?? charVk(key);
    if (vk) push({ kind: "key", vk, down: false });
  }

  async function endSession() {
    if (!sid) return;
    if (lanBase) { try { await fetch(`${lanBase}/view/${sid}/end`, { method: "POST" }); } catch {} }
    try {
      await api(`/api/v3/remote/${sid}/end`, { method: "POST" });
    } catch {}
    navigate(session ? `/devices/${session.deviceUid}` : "/devices");
  }

  function startRecording() {
    const canvas = canvasRef.current;
    if (!canvas || recording) return;
    try {
      const stream = canvas.captureStream(8);
      const types = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
      const mime = types.find((t) => MediaRecorder.isTypeSupported(t)) || "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2500000 });
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `sessao-remota-${sid}.webm`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 15000);
        setRecording(false);
      };
      rec.start(1000);
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      setError("Nao foi possivel iniciar a gravacao: " + (err as Error).message);
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
    recorderRef.current = null;
  }

  const loadFiles = useCallback(async () => {
    try {
      const res = await api<{ files: RemoteFile[] }>(`/api/v3/remote/${sid}/files`);
      setFiles(res.files || []);
    } catch { /* ignore */ }
  }, [sid]);

  useEffect(() => {
    if (!showFiles) return;
    void loadFiles();
    const id = window.setInterval(() => void loadFiles(), 3000);
    return () => window.clearInterval(id);
  }, [showFiles, loadFiles]);

  async function uploadFile(file: File) {
    if (!sid || !file) return;
    if (file.size > 32 * 1024 * 1024) {
      setError("Arquivo maior que 32 MB.");
      return;
    }
    setFileBusy(true);
    setError(null);
    try {
      if (lanBase) {
        // LAN: sobe os bytes para o relay ANTES de avisar a nuvem, para o
        // computador remoto nunca tentar baixar antes do arquivo estar la.
        const uid = "RF-" + Array.from(crypto.getRandomValues(new Uint8Array(6)))
          .map((b) => b.toString(16).toUpperCase().padStart(2, "0"))
          .join("")
          .slice(0, 12);
        const up = await fetch(`${lanBase}/file/${sid}/${uid}`, {
          method: "POST",
          headers: { "content-type": "application/octet-stream" },
          body: file,
        });
        if (!up.ok) throw new Error("falha ao enviar para o relay local");
        await api(`/api/v3/remote/${sid}/files`, {
          method: "POST",
          body: { uid, name: file.name, dest: uploadDest.trim(), viaRelay: true, size: file.size },
        });
      } else {
        const dataBase64 = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result || ""));
          r.onerror = () => reject(new Error("falha ao ler o arquivo"));
          r.readAsDataURL(file);
        });
        await api(`/api/v3/remote/${sid}/files`, {
          method: "POST",
          body: { name: file.name, dataBase64, dest: uploadDest.trim() },
        });
      }
      await loadFiles();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao enviar o arquivo");
    } finally {
      setFileBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function requestDownload() {
    if (!sid || !downloadPath.trim()) return;
    setFileBusy(true);
    setError(null);
    try {
      await api(`/api/v3/remote/${sid}/download`, {
        method: "POST",
        body: { path: downloadPath.trim() },
      });
      await loadFiles();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao solicitar o arquivo");
    } finally {
      setFileBusy(false);
    }
  }

  async function saveRemoteFile(f: RemoteFile) {
    try {
      const url = lanBase
        ? `${lanBase}/file/${sid}/${f.uid}`
        : `${apiBase()}/api/v3/remote/${sid}/files/${f.uid}`;
      const res = lanBase
        ? await fetch(url, { cache: "no-store" })
        : null;
      const blob = lanBase
        ? await (res as Response).blob()
        : await apiBlob(`/api/v3/remote/${sid}/files/${f.uid}`);
      if (lanBase && res && !res.ok) throw new Error("arquivo nao esta mais no relay (tempo expirado)");
      const link = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = link;
      a.download = f.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(link), 15000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao baixar o arquivo");
    }
  }

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    recorderRef.current?.stop();
  }, []);

  if (!can("actions.issue")) {
    return (
      <div className="page">
        <ErrorAlert message="Seu papel nao permite acesso remoto." />
      </div>
    );
  }

  return (
    <div className="page" style={{ maxWidth: "none", padding: isFullscreen ? 0 : 16 }}>
      <div className="toolbar" style={{ marginBottom: isFullscreen ? 0 : 12, ...(isFullscreen ? { position: "fixed", top: 8, left: 8, right: 8, zIndex: 50, background: "rgba(12,20,31,0.82)", padding: "6px 10px", borderRadius: 8 } : {}) }}>
        <strong>Acesso remoto</strong>
        <span className="subtle">
          {session?.deviceName || session?.deviceUid || sid} · <Badge value={ended ? "offline" : session?.status === "active" ? "online" : "warning"} label={ended ? "encerrada" : status} />
          {frames ? ` · ${frames} frames` : ""}
          {lanBase ? " · LAN" : " · nuvem"}
        </span>
        <span className="spacer" />
        {session?.deviceUid ? (
          <button className="btn" type="button" onClick={() => navigate(`/devices/${session.deviceUid}`)}>
            Voltar ao dispositivo
          </button>
        ) : null}
        <button className="btn" type="button" onClick={toggleFullscreen} disabled={!frames}>
          {isFullscreen ? "Sair da tela cheia" : "Tela cheia"}
        </button>
        <button className={`btn ${recording ? "danger" : ""}`} type="button" onClick={recording ? stopRecording : startRecording} disabled={!frames}>
          {recording ? "Parar gravacao" : "Gravar tela"}
        </button>
        <button className={`btn ${privacyOn ? "primary" : ""}`} type="button" onClick={togglePrivacy}>
          {privacyOn ? "Privacidade: ativada" : "Privacidade: desativada"}
        </button>
        <button className="btn" type="button" onClick={() => setShowFiles((v) => !v)}>
          {showFiles ? "Ocultar arquivos" : "Arquivos"}
        </button>
        <button className="btn danger" type="button" onClick={endSession}>
          Encerrar sessao
        </button>
      </div>

      {error ? <ErrorAlert message={error} /> : null}
      {ended ? <div className="alert info">Sessao encerrada.</div> : null}

      <div
        ref={boxRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          outline: focused ? "2px solid var(--brand-600)" : "1px solid var(--border)",
          borderRadius: 8,
          background: "#000",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 320,
          height: isFullscreen ? "100vh" : undefined,
          overflow: "hidden",
        }}
      >
        {!frames ? (
          <div style={{ color: "#94a3b8", padding: 24, textAlign: "center" }}>
            {ended ? "Sessao encerrada." : <><Spinner /> Conectando ao computador...<div className="subtle" style={{ marginTop: 8 }}>A sessao inicia no proximo check-in (ate 30s). Clique na tela para enviar teclado.</div></>}
          </div>
        ) : null}
        <canvas
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onWheel={onWheel}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            display: frames ? "block" : "none",
            width: "100%",
            height: isFullscreen ? "100vh" : "auto",
            maxHeight: isFullscreen ? "100vh" : "calc(100vh - 190px)",
            objectFit: "contain",
            cursor: "default",
          }}
        />
      </div>
      <p className="subtle" style={{ marginTop: 8, fontSize: 12 }}>
        Clique na tela para capturar o teclado (Esc/Tab/Setas sao enviados). Arraste para mover; clique para clicar.
      </p>

      {showFiles ? (
        <Card title="Arquivos" >
          <div className="field" style={{ marginBottom: 8 }}>
            <label htmlFor="upload-dest">Pasta de destino no computador</label>
            <input
              id="upload-dest"
              className="input mono"
              value={uploadDest}
              onChange={(e) => setUploadDest(e.target.value)}
              placeholder="%DOWNLOADS% ou C:\Users\Public\Downloads"
              disabled={ended}
            />
            <div className="toolbar" style={{ marginTop: 6 }}>
              <button className="btn sm" type="button" onClick={() => setUploadDest("%DOWNLOADS%")} disabled={ended}>Downloads do usuario</button>
              <button className="btn sm" type="button" onClick={() => setUploadDest("%DESKTOP%")} disabled={ended}>Area de Trabalho</button>
              <button className="btn sm" type="button" onClick={() => setUploadDest("%DOCUMENTS%")} disabled={ended}>Documentos</button>
              <button className="btn sm" type="button" onClick={() => setUploadDest("C:\\Users\\Public\\Downloads")} disabled={ended}>Publico</button>
            </div>
          </div>
          <div className="toolbar" style={{ marginBottom: isFullscreen ? 0 : 12, ...(isFullscreen ? { position: "fixed", top: 8, left: 8, right: 8, zIndex: 50, background: "rgba(12,20,31,0.82)", padding: "6px 10px", borderRadius: 8 } : {}) }}>
            <input
              ref={fileInputRef}
              type="file"
              className="input"
              style={{ maxWidth: 320 }}
              disabled={fileBusy || ended}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadFile(f);
              }}
            />
            <span className="subtle" style={{ fontSize: 12 }}>
              {fileBusy ? "Enviando..." : `Destino: ${uploadDest || "C:\\Users\\Public\\Downloads"}`}
            </span>
          </div>
          <div className="toolbar" style={{ marginBottom: isFullscreen ? 0 : 12, ...(isFullscreen ? { position: "fixed", top: 8, left: 8, right: 8, zIndex: 50, background: "rgba(12,20,31,0.82)", padding: "6px 10px", borderRadius: 8 } : {}) }}>
            <input
              className="input mono"
              style={{ flex: 1, minWidth: 260 }}
              value={downloadPath}
              onChange={(e) => setDownloadPath(e.target.value)}
              placeholder="C:\caminho\do\arquivo"
              disabled={ended}
            />
            <button className="btn primary" type="button" onClick={requestDownload} disabled={fileBusy || ended}>
              {fileBusy ? "Solicitando..." : "Baixar do computador"}
            </button>
          </div>
          {files.length === 0 ? (
            <Empty>Nenhum arquivo transferido nesta sessao.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Arquivo</th><th>Direcao</th><th>Tamanho</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {files.map((f) => (
                    <tr key={f.uid}>
                      <td>
                        <div className="cell-title">{f.name}</div>
                        {f.direction === "to_device" && f.status === "sent" && f.finalPath ? (
                          <div className="cell-sub mono" style={{ color: "#2e7d32" }}>Salvo em: {f.finalPath}</div>
                        ) : (
                          <div className="cell-sub mono">{f.direction === "to_device" ? `Destino: ${f.path || "C:\\Users\\Public\\Downloads"}` : f.path}</div>
                        )}
                      </td>
                      <td className="cell-sub">{f.direction === "to_device" ? "Painel → PC" : "PC → Painel"}</td>
                      <td>{humanSize(f.sizeBytes)}</td>
                      <td>
                        <Badge value={f.status === "received" || f.status === "sent" ? "completed" : f.status === "failed" ? "failed" : "warning"} label={f.status === "pending" ? "enviando" : f.status === "requested" ? "preparando" : f.status === "sent" ? "entregue" : f.status === "received" ? "pronto" : f.status} />
                        {f.error ? <div className="cell-sub">{f.error}</div> : null}
                      </td>
                      <td>
                        {f.direction === "from_device" && f.status === "received" ? (
                          <button className="btn sm" type="button" onClick={() => void saveRemoteFile(f)}>
                            Baixar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
}
